import { useState, useMemo, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Logo from '@/components/feature/Logo';
import { countryCodes, citiesByCountry } from '@/mocks/countryCodes';
import { supabase } from '@/lib/supabase';

const businessCategoriesList: string[] = [
  'Money Transfer / Remittance',
  'Foreign Exchange Bureau',
  'Retail Banking',
  'Microfinance Institution',
  'Payment Service Provider',
  'Mobile Money Operator',
  'E-commerce Payments',
  'Travel & Tourism Agency',
  'Telecommunications Provider',
  'Retail Store',
  'Supermarket / Grocery Chain',
  'Pharmacy',
  'Gas Station / Fuel Retailer',
  'Post Office / Courier Service',
  'Insurance Services',
  'Import / Export Trading',
  'Logistics & Shipping',
  'Hospitality / Hotel',
  'Real Estate Agency',
  'Financial Advisory',
  'Other Financial Services',
];

type TabType = 'personal' | 'business';
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function SignupPage() {
  const [activeTab, setActiveTab] = useState<TabType>('personal');

  // Personal fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+961');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [kycFile, setKycFile] = useState<File | null>(null);

  // Business fields
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessCountryCode, setBusinessCountryCode] = useState('+961');
  const [businessLandline, setBusinessLandline] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [relevantExperience, setRelevantExperience] = useState('');
  const [motivation, setMotivation] = useState('');
  const [kycApproved, setKycApproved] = useState(false);
  const [businessDocsFile, setBusinessDocsFile] = useState<File | null>(null);
  const [ownerPassportFile, setOwnerPassportFile] = useState<File | null>(null);
  const [businessCountry, setBusinessCountry] = useState('');
  const [businessCity, setBusinessCity] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [bCountrySearch, setBCountrySearch] = useState('');
  const [bCitySearch, setBCitySearch] = useState('');
  const [bCatSearch, setBCatSearch] = useState('');
  const [bCountryDropdownOpen, setBCountryDropdownOpen] = useState(false);
  const [bCityDropdownOpen, setBCityDropdownOpen] = useState(false);
  const [bCatDropdownOpen, setBCatDropdownOpen] = useState(false);

  // Submission
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [error, setError] = useState('');
  const [formMsg, setFormMsg] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [submissionDate, setSubmissionDate] = useState('');

  // Auto-scroll to error messages so they're never missed
  useEffect(() => {
    if (error || formMsg) {
      const el = document.getElementById('form-error');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [error, formMsg]);

  // Dropdowns
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countryCodes;
    const q = countrySearch.toLowerCase();
    return countryCodes.filter((c) => c.country.toLowerCase().includes(q));
  }, [countrySearch]);

  const availableCities = useMemo(() => {
    if (!country) return [];
    const raw = citiesByCountry[country] || [];
    if (!citySearch.trim()) return raw;
    const q = citySearch.toLowerCase();
    return raw.filter((c) => c.toLowerCase().includes(q));
  }, [country, citySearch]);

  const filteredBCountries = useMemo(() => {
    if (!bCountrySearch.trim()) return countryCodes;
    const q = bCountrySearch.toLowerCase();
    return countryCodes.filter((c) => c.country.toLowerCase().includes(q));
  }, [bCountrySearch]);

  const availableBusinessCities = useMemo(() => {
    if (!businessCountry) return [];
    const raw = citiesByCountry[businessCountry] || [];
    if (!bCitySearch.trim()) return raw;
    const q = bCitySearch.toLowerCase();
    return raw.filter((c) => c.toLowerCase().includes(q));
  }, [businessCountry, bCitySearch]);

  const filteredBCategories = useMemo(() => {
    if (!bCatSearch.trim()) return businessCategoriesList;
    const q = bCatSearch.toLowerCase();
    return businessCategoriesList.filter((c) => c.toLowerCase().includes(q));
  }, [bCatSearch]);

  const generateRef = () => {
    const ts = Date.now().toString(36).toUpperCase().slice(-6);
    const rnd = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `TMT-${ts}-${rnd}`;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setFormMsg('');

    // -- Comprehensive JS Validation --
    if (activeTab === 'personal') {
      if (!firstName.trim()) {
        setError('Please enter your first name.');
        return;
      }
      if (!lastName.trim()) {
        setError('Please enter your last name.');
        return;
      }
      if (!email.trim()) {
        setError('Please enter your email address.');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address.');
        return;
      }
      if (!phone.trim()) {
        setError('Please enter your phone number.');
        return;
      }
      if (!country) {
        setError('Please select your country.');
        return;
      }
      if (!city) {
        setError('Please select your city.');
        return;
      }
      if (!address.trim()) {
        setError('Please enter your address.');
        return;
      }
      if (!password) {
        setError('Please enter a password.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      if (!kycFile) {
        setError('Please upload your Passport or National ID.');
        return;
      }
    }

    if (activeTab === 'business') {
      if (!businessName.trim()) {
        setError('Please enter your registered business name.');
        return;
      }
      if (!businessType) {
        setError('Please select a business type.');
        return;
      }
      if (!businessCategory) {
        setError('Please select a business market category.');
        return;
      }
      if (!businessCountry) {
        setError('Please select your business country.');
        return;
      }
      if (!businessCity) {
        setError('Please select your business city.');
        return;
      }
      if (!businessAddress.trim()) {
        setError('Please enter your registered business address.');
        return;
      }
      if (!businessEmail.trim()) {
        setError('Please enter your business email address.');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(businessEmail)) {
        setError('Please enter a valid business email address.');
        return;
      }
      if (!businessPhone.trim()) {
        setError('Please enter your business phone number.');
        return;
      }
      if (!motivation.trim()) {
        setError('Please tell us why you want to partner with TMT.');
        return;
      }
      if (!businessDocsFile) {
        setError('Please upload your Business License or Registration.');
        return;
      }
      if (!ownerPassportFile) {
        setError('Please upload the Owner / Director Passport.');
        return;
      }
      if (!kycApproved) {
        setError('Please confirm your business is KYC compliant.');
        return;
      }
    }

    if (!agreeTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }

    setSubmitStatus('submitting');

    try {
      const formEl = e.currentTarget;
      const formData = new FormData(formEl);

      // Honeypot check
      const honeypot = formData.get('company_alt') as string;
      if (honeypot && honeypot.trim() !== '') {
        const ref = generateRef();
        const now = new Date().toLocaleString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
        });
        setReferenceId(ref);
        setSubmissionDate(now);
        setSubmitStatus('success');
        return;
      }

      const referenceId = generateRef();

      // Build Supabase payload
      const supabasePayload: Record<string, unknown> = {
        account_type: activeTab,
        reference_id: referenceId,
      };

      if (activeTab === 'personal') {
        supabasePayload.first_name = firstName;
        supabasePayload.last_name = lastName;
        supabasePayload.email = email;
        supabasePayload.country_code = countryCode;
        supabasePayload.phone = phone;
        supabasePayload.country = country;
        supabasePayload.city = city;
        supabasePayload.address = address;
        supabasePayload.kyc_filename = kycFile?.name || null;
      } else {
        supabasePayload.business_name = businessName;
        supabasePayload.business_type = businessType;
        supabasePayload.business_category = businessCategory;
        supabasePayload.business_country = businessCountry;
        supabasePayload.business_city = businessCity;
        supabasePayload.business_address = businessAddress;
        supabasePayload.email = businessEmail;
        supabasePayload.business_email = businessEmail;
        supabasePayload.business_phone = businessPhone;
        supabasePayload.business_country_code = businessCountryCode;
        supabasePayload.business_landline = businessLandline;
        supabasePayload.business_website = businessWebsite;
        supabasePayload.relevant_experience = relevantExperience;
        supabasePayload.motivation = motivation;
        supabasePayload.business_docs_filename = businessDocsFile?.name || null;
        supabasePayload.owner_passport_filename = ownerPassportFile?.name || null;
      }

      // Save to Supabase first — this is what the admin panel reads from
      const { error: supabaseErr } = await supabase
        .from('signup_applications')
        .insert([supabasePayload]);

      if (supabaseErr) {
        console.error('Supabase signup insert error:', supabaseErr);
        setSubmitStatus('error');
        setFormMsg(supabaseErr.message || 'Database error. Please try again.');
        return;
      }

      // Also submit to Readdy form API as secondary
      const submitUrl = activeTab === 'personal'
        ? 'https://readdy.ai/api/form/d9g969sugrdi4rjg5510'
        : 'https://readdy.ai/api/form/d9g969sugrdi4rjg551g';

      try {
        formData.delete('company_alt');
        const res = await fetch(submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        });

        const responseText = await res.text();
        let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
        try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

        if (!res.ok || parsed?.code !== 'OK') {
          console.warn('Readdy form API returned non-OK:', responseText);
        }
      } catch (err) {
        console.warn('Readdy form API call failed:', err);
      }

      const now = new Date().toLocaleString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
      });
      setReferenceId(referenceId);
      setSubmissionDate(now);
      setSubmitStatus('success');
    } catch (err: any) {
      console.error('Signup submit exception:', err);
      setSubmitStatus('error');
      setFormMsg(err?.message || 'Something went wrong. Please try again or contact us directly.');
    }
  };

  // Success state
  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <Logo size="md" />
          </div>
          <div className="bg-background-100 border border-primary-800/20 rounded-2xl p-8 md:p-10 text-center">
            <div className="mx-auto mb-6 w-16 h-16 bg-primary-500/15 border border-primary-500/30 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-primary-400 text-3xl" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
              {activeTab === 'personal' ? 'Account Request Sent Successfully' : 'Business Application Submitted'}
            </h1>
            <p className="text-foreground-300 text-sm md:text-base font-body mb-6 max-w-lg mx-auto">
              {activeTab === 'personal'
                ? 'Your personal account creation request has been submitted. Our team will verify your details and activate your account shortly.'
                : 'Your business account application has been received. Our partnerships team will review your submission and reach out within 48 hours.'}
            </p>
            <div className="inline-flex items-center gap-3 bg-background-50 border border-primary-800/20 rounded-xl px-5 py-3 mb-8">
              <span className="font-label text-xs tracking-wider uppercase text-foreground-400">Reference</span>
              <span className="font-heading text-lg font-bold text-primary-400 tracking-wider">{referenceId}</span>
            </div>
            <div className="text-foreground-500 text-xs font-body mb-8">Submitted on {submissionDate}</div>
            <div className="mt-6 pt-6 border-t border-primary-800/15">
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap">
                <i className="ri-home-line" />
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Logo size="md" />
        </div>

        <div className="bg-background-100 border border-primary-800/20 rounded-2xl p-6 md:p-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 text-center mb-2">
            Open Your Account
          </h1>
          <p className="text-foreground-400 text-sm text-center mb-6 font-body">
            {activeTab === 'personal'
              ? 'Create your personal TMT account and start transacting securely across 14+ corridors'
              : 'Register your business and become an authorized TMT money transfer agent'}
          </p>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-7">
            <div className="inline-flex bg-background-50 border border-primary-800/20 rounded-full p-1">
              <button type="button"
                onClick={() => { setActiveTab('personal'); setError(''); setFormMsg(''); }}
                className={`px-5 py-2 rounded-full font-label text-xs tracking-wider uppercase transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'personal'
                    ? 'bg-primary-500 text-background-50 font-semibold'
                    : 'text-foreground-400 hover:text-foreground-200 hover:bg-primary-700/10'
                }`}>
                <i className="ri-user-line mr-1.5" />Personal
              </button>
              <button type="button"
                onClick={() => { setActiveTab('business'); setError(''); setFormMsg(''); }}
                className={`px-5 py-2 rounded-full font-label text-xs tracking-wider uppercase transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'business'
                    ? 'bg-primary-500 text-background-50 font-semibold'
                    : 'text-foreground-400 hover:text-foreground-200 hover:bg-primary-700/10'
                }`}>
                <i className="ri-building-line mr-1.5" />Business
              </button>
            </div>
          </div>

          <form noValidate onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot */}
            <div className="hp-field">
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            {/* ============ PERSONAL TAB ============ */}
            {activeTab === 'personal' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">First Name *</label>
                    <input type="text" id="firstName" name="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="First name" />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Last Name *</label>
                    <input type="text" id="lastName" name="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Last name" />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Email Address *</label>
                  <input type="email" id="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="you@example.com" />
                </div>

                <div>
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Phone Number *</label>
                  <div className="flex gap-2">
                    <select id="countryCode" name="countryCode" value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                      className="w-[130px] flex-shrink-0 px-3 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body focus:outline-none focus:border-primary-500 transition-colors">
                      {[...countryCodes].sort((a, b) => a.country.localeCompare(b.country)).map((c) => (
                        <option key={c.code} value={c.code}>{c.code} {c.country}</option>
                      ))}
                    </select>
                    <input type="tel" id="phone" name="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required
                      className="flex-1 px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Enter your mobile number" />
                  </div>
                </div>

                {/* Country */}
                <div className="relative">
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Country *</label>
                  <input type="hidden" name="country" value={country} />
                  <div className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body cursor-pointer flex items-center justify-between focus-within:border-primary-500 transition-colors"
                    onClick={() => { setCountryDropdownOpen(!countryDropdownOpen); setCityDropdownOpen(false); }}>
                    <span className={country ? 'text-foreground-100' : 'text-foreground-600'}>{country || 'Select your country'}</span>
                    <i className={`${countryDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`} />
                  </div>
                  {countryDropdownOpen && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-background-100 border border-primary-800/30 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-primary-800/10">
                        <input type="text" value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} placeholder="Search country..."
                          className="w-full px-3 py-2 bg-background-50 border border-primary-800/20 rounded-md text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          onClick={(ev) => ev.stopPropagation()} />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredCountries.length === 0 ? (
                          <p className="px-4 py-3 text-foreground-500 text-sm">No countries found</p>
                        ) : (
                          filteredCountries.map((c) => (
                            <button key={c.code} type="button"
                              className={`w-full text-left px-4 py-2.5 text-sm font-body hover:bg-primary-500/10 transition-colors whitespace-nowrap ${country === c.country ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-200'}`}
                              onClick={() => { setCountry(c.country); setCity(''); setCountryDropdownOpen(false); setCountrySearch(''); }}>
                              {c.country}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* City */}
                <div className="relative">
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">City *</label>
                  <input type="hidden" name="city" value={city} />
                  <div className={`w-full px-4 py-3 border rounded-lg text-sm font-body flex items-center justify-between transition-colors ${
                    !country ? 'bg-background-200/50 border-primary-800/15 text-foreground-500 cursor-not-allowed'
                      : 'bg-background-50 border-primary-800/30 text-foreground-100 cursor-pointer focus-within:border-primary-500'}`}
                    onClick={() => { if (!country) return; setCityDropdownOpen(!cityDropdownOpen); setCountryDropdownOpen(false); }}>
                    <span className={city ? 'text-foreground-100' : 'text-foreground-600'}>{city || (country ? 'Select your city' : 'Select a country first')}</span>
                    <i className={`${cityDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`} />
                  </div>
                  {cityDropdownOpen && country && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-background-100 border border-primary-800/30 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-primary-800/10">
                        <input type="text" value={citySearch} onChange={(e) => setCitySearch(e.target.value)} placeholder="Search city..."
                          className="w-full px-3 py-2 bg-background-50 border border-primary-800/20 rounded-md text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          onClick={(ev) => ev.stopPropagation()} />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {availableCities.length === 0 ? (
                          <p className="px-4 py-3 text-foreground-500 text-sm">No cities found</p>
                        ) : (
                          availableCities.map((ct) => (
                            <button key={ct} type="button"
                              className={`w-full text-left px-4 py-2.5 text-sm font-body hover:bg-primary-500/10 transition-colors whitespace-nowrap ${city === ct ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-200'}`}
                              onClick={() => { setCity(ct); setCityDropdownOpen(false); setCitySearch(''); }}>
                              {ct}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Address *</label>
                  <input type="text" id="address" name="address" value={address} onChange={(e) => setAddress(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Street name, building number, apartment" />
                </div>

                {/* KYC Upload — no required attribute on hidden input, validated in JS */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block font-label text-xs tracking-wider uppercase text-foreground-300">Passport / National ID</label>
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-900 text-[0.6rem] font-label tracking-wider uppercase rounded-full">KYC Required</span>
                  </div>
                  <div className="relative">
                    <input type="file" id="kycUpload" name="kycUpload" accept="image/*,.pdf"
                      onChange={(e) => { if (e.target.files?.[0]) setKycFile(e.target.files[0]); }}
                      className="hidden" />
                    <label htmlFor="kycUpload"
                      className={`flex items-center justify-center gap-2 w-full px-4 py-3 bg-background-50 border border-dashed rounded-lg text-sm font-body transition-colors cursor-pointer ${
                        kycFile
                          ? 'border-primary-500 text-foreground-200'
                          : 'border-primary-700/40 text-foreground-400 hover:border-primary-500 hover:text-foreground-200'
                      }`}>
                      <i className="ri-upload-cloud-line text-primary-400 text-lg" />
                      {kycFile ? kycFile.name : 'Upload Passport or National ID'}
                    </label>
                  </div>
                  <p className="text-foreground-600 text-[0.65rem] mt-1 font-body">Accepted formats: JPG, PNG, PDF. Max size: 5MB.</p>
                  {!kycFile && (
                    <p className="text-red-400 text-[0.65rem] mt-1 font-body">This field is required.</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Password *</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors pr-12"
                      placeholder="Min. 8 characters" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 hover:text-primary-400 transition-colors">
                      <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line`} />
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Confirm Password *</label>
                  <input type="password" id="confirmPassword" name="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Confirm password" />
                </div>
              </>
            )}

            {/* ============ BUSINESS TAB ============ */}
            {activeTab === 'business' && (
              <>
                <div>
                  <label htmlFor="businessName" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Registered Business Name *</label>
                  <input type="text" id="businessName" name="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Official business or trade name as registered" />
                </div>

                <div>
                  <label htmlFor="businessType" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business Type *</label>
                  <select id="businessType" name="businessType" value={businessType} onChange={(e) => setBusinessType(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body focus:outline-none focus:border-primary-500 transition-colors appearance-none">
                    <option value="">Select business type...</option>
                    <option value="retail_store">Retail Store</option>
                    <option value="money_transfer_outlet">Money Transfer Outlet</option>
                    <option value="service_center">Service Center</option>
                    <option value="business_entity">Business Entity</option>
                    <option value="independent_agent">Independent Agent</option>
                  </select>
                </div>

                {/* Business Category */}
                <div className="relative">
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business Market Category *</label>
                  <input type="hidden" name="businessCategory" value={businessCategory} />
                  <div className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body cursor-pointer flex items-center justify-between focus-within:border-primary-500 transition-colors"
                    onClick={() => { setBCatDropdownOpen(!bCatDropdownOpen); setBCountryDropdownOpen(false); setBCityDropdownOpen(false); }}>
                    <span className={businessCategory ? 'text-foreground-100' : 'text-foreground-600'}>{businessCategory || 'Select your market category'}</span>
                    <i className={`${bCatDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`} />
                  </div>
                  {bCatDropdownOpen && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-background-100 border border-primary-800/30 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-primary-800/10">
                        <input type="text" value={bCatSearch} onChange={(e) => setBCatSearch(e.target.value)} placeholder="Search category..."
                          className="w-full px-3 py-2 bg-background-50 border border-primary-800/20 rounded-md text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          onClick={(ev) => ev.stopPropagation()} />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredBCategories.length === 0 ? (
                          <p className="px-4 py-3 text-foreground-500 text-sm">No categories found</p>
                        ) : (
                          filteredBCategories.map((cat) => (
                            <button key={cat} type="button"
                              className={`w-full text-left px-4 py-2.5 text-sm font-body hover:bg-primary-500/10 transition-colors whitespace-nowrap ${businessCategory === cat ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-200'}`}
                              onClick={() => { setBusinessCategory(cat); setBCatDropdownOpen(false); setBCatSearch(''); }}>
                              {cat}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Business Country */}
                <div className="relative">
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business Country *</label>
                  <input type="hidden" name="businessCountry" value={businessCountry} />
                  <div className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body cursor-pointer flex items-center justify-between focus-within:border-primary-500 transition-colors"
                    onClick={() => { setBCountryDropdownOpen(!bCountryDropdownOpen); setBCityDropdownOpen(false); setBCatDropdownOpen(false); }}>
                    <span className={businessCountry ? 'text-foreground-100' : 'text-foreground-600'}>{businessCountry || 'Select business country'}</span>
                    <i className={`${bCountryDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`} />
                  </div>
                  {bCountryDropdownOpen && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-background-100 border border-primary-800/30 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-primary-800/10">
                        <input type="text" value={bCountrySearch} onChange={(e) => setBCountrySearch(e.target.value)} placeholder="Search country..."
                          className="w-full px-3 py-2 bg-background-50 border border-primary-800/20 rounded-md text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          onClick={(ev) => ev.stopPropagation()} />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredBCountries.length === 0 ? (
                          <p className="px-4 py-3 text-foreground-500 text-sm">No countries found</p>
                        ) : (
                          filteredBCountries.map((c) => (
                            <button key={c.code} type="button"
                              className={`w-full text-left px-4 py-2.5 text-sm font-body hover:bg-primary-500/10 transition-colors whitespace-nowrap ${businessCountry === c.country ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-200'}`}
                              onClick={() => { setBusinessCountry(c.country); setBusinessCity(''); setBCountryDropdownOpen(false); setBCountrySearch(''); }}>
                              {c.country}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Business City */}
                <div className="relative">
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business City *</label>
                  <input type="hidden" name="businessCity" value={businessCity} />
                  <div className={`w-full px-4 py-3 border rounded-lg text-sm font-body flex items-center justify-between transition-colors ${
                    !businessCountry ? 'bg-background-200/50 border-primary-800/15 text-foreground-500 cursor-not-allowed'
                      : 'bg-background-50 border-primary-800/30 text-foreground-100 cursor-pointer focus-within:border-primary-500'}`}
                    onClick={() => { if (!businessCountry) return; setBCityDropdownOpen(!bCityDropdownOpen); setBCountryDropdownOpen(false); setBCatDropdownOpen(false); }}>
                    <span className={businessCity ? 'text-foreground-100' : 'text-foreground-600'}>{businessCity || (businessCountry ? 'Select business city' : 'Select a country first')}</span>
                    <i className={`${bCityDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`} />
                  </div>
                  {bCityDropdownOpen && businessCountry && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-background-100 border border-primary-800/30 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-primary-800/10">
                        <input type="text" value={bCitySearch} onChange={(e) => setBCitySearch(e.target.value)} placeholder="Search city..."
                          className="w-full px-3 py-2 bg-background-50 border border-primary-800/20 rounded-md text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          onClick={(ev) => ev.stopPropagation()} />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {availableBusinessCities.length === 0 ? (
                          <p className="px-4 py-3 text-foreground-500 text-sm">No cities found</p>
                        ) : (
                          availableBusinessCities.map((ct) => (
                            <button key={ct} type="button"
                              className={`w-full text-left px-4 py-2.5 text-sm font-body hover:bg-primary-500/10 transition-colors whitespace-nowrap ${businessCity === ct ? 'bg-primary-500/15 text-primary-400' : 'text-foreground-200'}`}
                              onClick={() => { setBusinessCity(ct); setBCityDropdownOpen(false); setBCitySearch(''); }}>
                              {ct}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="businessAddress" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Registered Business Address *</label>
                  <input type="text" id="businessAddress" name="businessAddress" value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Street name, building number, postal code" />
                </div>

                <div>
                  <label htmlFor="businessEmail" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business Email Address *</label>
                  <input type="email" id="businessEmail" name="businessEmail" value={businessEmail} onChange={(e) => setBusinessEmail(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="business@company.com" />
                </div>

                <div>
                  <label htmlFor="businessWebsite" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business Website URL</label>
                  <input type="url" id="businessWebsite" name="businessWebsite" value={businessWebsite} onChange={(e) => setBusinessWebsite(e.target.value)}
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="https://www.yourbusiness.com" />
                </div>

                <div>
                  <label className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Business Phone *</label>
                  <div className="flex gap-2">
                    <select id="businessCountryCode" name="businessCountryCode" value={businessCountryCode} onChange={(e) => setBusinessCountryCode(e.target.value)}
                      className="w-[130px] flex-shrink-0 px-3 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body focus:outline-none focus:border-primary-500 transition-colors">
                      {[...countryCodes].sort((a, b) => a.country.localeCompare(b.country)).map((c) => (
                        <option key={c.code} value={c.code}>{c.code} {c.country}</option>
                      ))}
                    </select>
                    <input type="tel" id="businessPhone" name="businessPhone" value={businessPhone} onChange={(e) => setBusinessPhone(e.target.value)} required
                      className="flex-1 px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Enter business phone number" />
                  </div>
                </div>

                <div>
                  <label htmlFor="businessLandline" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Landline / Alternate Phone</label>
                  <input type="tel" id="businessLandline" name="businessLandline" value={businessLandline} onChange={(e) => setBusinessLandline(e.target.value)}
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Optional landline number" />
                </div>

                <div>
                  <label htmlFor="relevantExperience" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Relevant Industry Experience</label>
                  <textarea id="relevantExperience" name="relevantExperience" rows={3} maxLength={500}
                    value={relevantExperience} onChange={(e) => setRelevantExperience(e.target.value)}
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="Describe your experience in money transfer, financial services, foreign exchange, retail banking, or related industries" />
                  <p className="text-foreground-600 text-[0.65rem] mt-1 font-body text-right">{relevantExperience.length}/500</p>
                </div>

                <div>
                  <label htmlFor="motivation" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">Why partner with TMT? *</label>
                  <textarea id="motivation" name="motivation" rows={3} maxLength={500}
                    value={motivation} onChange={(e) => setMotivation(e.target.value)} required
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="Tell us about your business goals and why you want to join the TMT network" />
                  <p className="text-foreground-600 text-[0.65rem] mt-1 font-body text-right">{motivation.length}/500</p>
                </div>

                {/* KYC Compliance */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="kycApproved" checked={kycApproved} onChange={(e) => setKycApproved(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-primary-500 rounded border-primary-700/30" />
                  <span className="text-foreground-300 text-sm font-body leading-relaxed">
                    I confirm that my business is <strong className="text-foreground-100">KYC compliant</strong> and all beneficial
                    owners have completed Know Your Customer verification as required by applicable financial regulations.
                  </span>
                </label>

                {/* Business Documents — no required on hidden input */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block font-label text-xs tracking-wider uppercase text-foreground-300">Business License / Registration</label>
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-900 text-[0.6rem] font-label tracking-wider uppercase rounded-full">Required</span>
                  </div>
                  <div className="relative">
                    <input type="file" id="businessDocs" name="businessDocs" accept="image/*,.pdf"
                      onChange={(e) => { if (e.target.files?.[0]) setBusinessDocsFile(e.target.files[0]); }}
                      className="hidden" />
                    <label htmlFor="businessDocs"
                      className={`flex items-center justify-center gap-2 w-full px-4 py-3 bg-background-50 border border-dashed rounded-lg text-sm font-body transition-colors cursor-pointer ${
                        businessDocsFile
                          ? 'border-primary-500 text-foreground-200'
                          : 'border-primary-700/40 text-foreground-400 hover:border-primary-500 hover:text-foreground-200'
                      }`}>
                      <i className="ri-file-text-line text-primary-400 text-lg" />
                      {businessDocsFile ? businessDocsFile.name : 'Upload Business License or Certificate of Incorporation'}
                    </label>
                  </div>
                  <p className="text-foreground-600 text-[0.65rem] mt-1 font-body">JPG, PNG, or PDF. Max 10MB.</p>
                  {!businessDocsFile && (
                    <p className="text-red-400 text-[0.65rem] mt-1 font-body">This field is required.</p>
                  )}
                </div>

                {/* Owner Passport — no required on hidden input */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block font-label text-xs tracking-wider uppercase text-foreground-300">Owner / Director Passport</label>
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-900 text-[0.6rem] font-label tracking-wider uppercase rounded-full">Required</span>
                  </div>
                  <div className="relative">
                    <input type="file" id="ownerPassport" name="ownerPassport" accept="image/*,.pdf"
                      onChange={(e) => { if (e.target.files?.[0]) setOwnerPassportFile(e.target.files[0]); }}
                      className="hidden" />
                    <label htmlFor="ownerPassport"
                      className={`flex items-center justify-center gap-2 w-full px-4 py-3 bg-background-50 border border-dashed rounded-lg text-sm font-body transition-colors cursor-pointer ${
                        ownerPassportFile
                          ? 'border-primary-500 text-foreground-200'
                          : 'border-primary-700/40 text-foreground-400 hover:border-primary-500 hover:text-foreground-200'
                      }`}>
                      <i className="ri-passport-line text-primary-400 text-lg" />
                      {ownerPassportFile ? ownerPassportFile.name : 'Upload Passport or National ID of Business Owner'}
                    </label>
                  </div>
                  <p className="text-foreground-600 text-[0.65rem] mt-1 font-body">Clear copy of valid government-issued ID. JPG, PNG, PDF — max 5MB.</p>
                  {!ownerPassportFile && (
                    <p className="text-red-400 text-[0.65rem] mt-1 font-body">This field is required.</p>
                  )}
                </div>
              </>
            )}

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" name="agreeTerms" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-primary-500 rounded border-primary-700/30" />
              <span className="text-foreground-400 text-xs font-body leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-400 hover:text-primary-300 transition-colors">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary-400 hover:text-primary-300 transition-colors">Privacy Policy</Link>
                {activeTab === 'business' && ' and confirm that all information provided is accurate and truthful.'}
              </span>
            </label>

            {/* Error display */}
            {(error || formMsg) && (
              <div id="form-error" className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2 animate-fade-in-up">
                <i className="ri-error-warning-line text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-400 text-sm font-body">{error || formMsg}</p>
              </div>
            )}

            <button type="submit" disabled={submitStatus === 'submitting'}
              className="w-full py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap">
              {submitStatus === 'submitting' ? (
                <><i className="ri-loader-4-line animate-spin" />Submitting...</>
              ) : activeTab === 'business' ? (
                'Register Business Account'
              ) : (
                'Create Personal Account'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-primary-800/20 text-center">
            <p className="text-foreground-400 text-sm font-body">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors font-semibold">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}